import "@podcrash/ui/base.css";

/**
 * Public-page islands.
 *
 * Public pages are server-rendered HTML (ADR 0009); this is the only JavaScript a reader
 * downloads, and it exists solely for the few controls that genuinely need state. Keeping
 * it small is the point — an article should not ship an editor.
 */

/**
 * Collapses the three-column article layout below the compact breakpoint.
 *
 * The grid is expressed inline to match the prototype, so it cannot be handled by a media
 * query in the stylesheet alone. This mirrors what the prototype does in JavaScript.
 */
function initResponsiveArticleGrid(): void {
  const grids = document.querySelectorAll<HTMLElement>("[data-article-grid]");
  const searchGrids = document.querySelectorAll<HTMLElement>("[data-search-grid]");
  const newsGrids = document.querySelectorAll<HTMLElement>("[data-news-grid]");
  if (grids.length === 0 && searchGrids.length === 0 && newsGrids.length === 0) return;

  const compact = window.matchMedia("(max-width: 979px)");

  const apply = (): void => {
    for (const grid of grids) {
      grid.style.gridTemplateColumns = compact.matches
        ? "minmax(0,1fr)"
        : "190px minmax(0,1fr) 360px";
      // The article and infobox are placed in explicit columns so the layout needs no empty
      // spacer element when a page has no contents list or no infobox. Those fixed columns have
      // to be released when the grid collapses to a single column, or the two would sit in
      // phantom tracks instead of stacking.
      const article = grid.querySelector<HTMLElement>("article[data-page-id]");
      if (article) article.style.gridColumn = compact.matches ? "auto" : "2";
      const infobox = grid.querySelector<HTMLElement>("[data-infobox]");
      if (infobox) infobox.style.gridColumn = compact.matches ? "auto" : "3";
    }
    for (const grid of searchGrids) {
      grid.style.gridTemplateColumns = compact.matches ? "minmax(0,1fr)" : "218px minmax(0,1fr)";
    }
    for (const grid of newsGrids) {
      grid.style.gridTemplateColumns = compact.matches ? "minmax(0,1fr)" : "minmax(0,1fr) 290px";
    }
    for (const toc of document.querySelectorAll<HTMLElement>("[data-toc]")) {
      toc.style.display = compact.matches ? "none" : "block";
      toc.style.position = compact.matches ? "static" : "sticky";
    }
  };

  apply();
  compact.addEventListener("change", apply);
}

/**
 * Highlights the table-of-contents entry for the section currently in view.
 *
 * Uses IntersectionObserver rather than a scroll handler so it costs nothing while the
 * reader is stationary, which is most of the time on a long article.
 */
function initTocHighlight(): void {
  const links = [...document.querySelectorAll<HTMLAnchorElement>("[data-toc] a")];
  if (links.length === 0) return;

  const byId = new Map(links.map((link) => [link.getAttribute("href")?.slice(1) ?? "", link]));
  const headings = [...byId.keys()]
    .map((id) => document.getElementById(id))
    .filter((element): element is HTMLElement => element !== null);

  if (headings.length === 0) return;

  const setActive = (id: string): void => {
    for (const [linkId, link] of byId) {
      const active = linkId === id;
      link.style.borderLeftColor = active ? "var(--orange)" : "transparent";
      link.style.color = active ? "var(--ink)" : "var(--ink2)";
      link.style.fontWeight = active ? "700" : "500";
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (visible.length > 0) setActive(visible[0]!.target.id);
    },
    // Biased to the top of the viewport: the heading a reader is "at" is the one just above
    // the fold, not one centred on screen.
    { rootMargin: "-88px 0px -70% 0px", threshold: 0 },
  );

  for (const heading of headings) observer.observe(heading);
}

interface LiveSearchHit {
  type: string;
  path: string;
  title: string;
  productName: string | null;
  accentColour: string | null;
}

const SEARCH_TYPE_LABELS: Record<string, string> = {
  article: "Article",
  product: "Product",
  category: "Category",
  news: "News",
};

/**
 * Live search for the header field, opened with `/` — the prototype's command palette.
 *
 * As the reader types, results stream into a panel below the field and render one at a time,
 * so matches show up as they are found rather than after a full round-trip. This is strictly
 * an enhancement: the header is a real GET form, so with JavaScript disabled — or if the
 * streaming request fails — pressing Enter still submits to `/search` and returns the same
 * results server-side.
 */
function initSearchOverlay(): void {
  const form = document.querySelector<HTMLFormElement>("[data-header-search]");
  const input = form?.querySelector<HTMLInputElement>("input[name=q]");
  if (!form || !input) return;

  buildSearchPanel(form, input);

  document.addEventListener("keydown", (event) => {
    // Ignore the hotkey while the reader is typing somewhere else, and never swallow a
    // browser or OS shortcut.
    const target = event.target as HTMLElement | null;
    const isTyping =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target?.isContentEditable === true;

    if (event.key === "/" && !isTyping && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      input.focus();
      input.select();
    }
  });
}

/**
 * Creates the results panel and wires the field to it. Returns nothing the caller needs — the
 * closure owns all the state (in-flight request, current selection) so nothing leaks onto the
 * module scope.
 */
function buildSearchPanel(form: HTMLFormElement, input: HTMLInputElement): void {
  const panel = document.createElement("div");
  panel.className = "wiki-search-panel";
  panel.hidden = true;
  panel.id = "wiki-search-panel";

  const status = document.createElement("p");
  status.className = "wiki-search-panel__status";
  status.setAttribute("aria-live", "polite");

  const list = document.createElement("div");
  list.className = "wiki-search-panel__list";
  list.setAttribute("role", "listbox");
  list.id = "wiki-search-listbox";
  list.setAttribute("aria-label", "Search results");

  const seeAll = document.createElement("a");
  seeAll.className = "wiki-search-panel__all";
  seeAll.hidden = true;

  panel.append(status, list, seeAll);
  form.append(panel);

  // Wire the field up as a combobox controlling the listbox (WAI-ARIA), so a screen reader
  // announces the panel and the active result.
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-controls", list.id);
  input.setAttribute("aria-autocomplete", "list");

  let rows: { el: HTMLAnchorElement; href: string }[] = [];
  let activeIndex = -1;
  let controller: AbortController | null = null;
  // Every request carries a token; a slow response for an earlier keystroke is discarded so
  // it cannot overwrite the panel with stale results.
  let requestToken = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  const open = (): void => {
    panel.hidden = false;
    input.setAttribute("aria-expanded", "true");
  };

  const close = (): void => {
    panel.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    activeIndex = -1;
  };

  const setActive = (next: number): void => {
    if (rows.length === 0) return;
    const clamped = (next + rows.length) % rows.length;
    rows.forEach((row, index) => {
      const active = index === clamped;
      row.el.classList.toggle("wiki-search-hit--active", active);
      row.el.setAttribute("aria-selected", String(active));
    });
    activeIndex = clamped;
    const activeRow = rows[clamped];
    if (activeRow) {
      input.setAttribute("aria-activedescendant", activeRow.el.id);
      activeRow.el.scrollIntoView({ block: "nearest" });
    }
  };

  const render = (query: string): void => {
    const trimmed = query.trim();
    seeAll.textContent = `See all results for “${trimmed}”`;
    seeAll.href = `/search?q=${encodeURIComponent(trimmed)}`;
  };

  const runSearch = (query: string): void => {
    const trimmed = query.trim();
    controller?.abort();

    if (trimmed === "") {
      close();
      list.replaceChildren();
      status.textContent = "";
      seeAll.hidden = true;
      rows = [];
      return;
    }

    const token = (requestToken += 1);
    controller = new AbortController();
    open();
    render(query);
    status.textContent = "Searching…";
    list.replaceChildren();
    seeAll.hidden = false;
    rows = [];
    activeIndex = -1;

    void streamSearch(trimmed, controller.signal, {
      onMeta: (total) => {
        if (token !== requestToken) return;
        status.textContent =
          total === 0
            ? `No results for “${trimmed}”`
            : `${total} ${total === 1 ? "result" : "results"}`;
      },
      onHit: (hit) => {
        if (token !== requestToken) return;
        const row = renderSearchHit(hit, rows.length);
        rows.push(row);
        list.append(row.el);
      },
    }).catch(() => {
      if (token !== requestToken) return;
      // The network failed or the response was malformed. Fall back to the plain form rather
      // than showing a broken panel: submitting still runs the same search server-side.
      close();
    });
  };

  input.addEventListener("input", () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSearch(input.value), 140);
  });

  input.addEventListener("focus", () => {
    if (input.value.trim() !== "" && rows.length > 0) open();
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      if (!panel.hidden) {
        event.preventDefault();
        close();
      } else {
        input.blur();
      }
      return;
    }

    if (panel.hidden) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(activeIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(activeIndex - 1);
    } else if (event.key === "Enter" && activeIndex >= 0) {
      // A highlighted result wins over submitting the form: jump straight to it.
      const activeRow = rows[activeIndex];
      if (activeRow) {
        event.preventDefault();
        window.location.assign(activeRow.href);
      }
    }
  });

  // Close when focus or a click leaves the search field and its panel.
  document.addEventListener("pointerdown", (event) => {
    if (!form.contains(event.target as Node)) close();
  });
}

/** One result row, rendered with DOM APIs so hit text is never interpolated into HTML. */
function renderSearchHit(hit: LiveSearchHit, index: number): { el: HTMLAnchorElement; href: string } {
  const el = document.createElement("a");
  el.className = "wiki-search-hit";
  el.href = hit.path;
  el.id = `wiki-search-hit-${index}`;
  el.setAttribute("role", "option");
  el.setAttribute("aria-selected", "false");

  const swatch = document.createElement("span");
  swatch.className = "wiki-search-hit__swatch";
  swatch.setAttribute("aria-hidden", "true");
  if (hit.accentColour) swatch.style.background = hit.accentColour;

  const text = document.createElement("span");
  text.className = "wiki-search-hit__text";

  const title = document.createElement("span");
  title.className = "wiki-search-hit__title";
  title.textContent = hit.title;

  const meta = document.createElement("span");
  meta.className = "wiki-search-hit__meta";
  const typeLabel = SEARCH_TYPE_LABELS[hit.type] ?? hit.type;
  meta.textContent = hit.productName
    ? `${typeLabel} · ${hit.productName}`
    : typeLabel;

  text.append(title, meta);
  el.append(swatch, text);

  return { el, href: hit.path };
}

/**
 * Reads the NDJSON search stream, invoking a callback per record as it arrives so the panel
 * fills in progressively. Rejects on a network error or a malformed line, which the caller
 * treats as "fall back to the plain form".
 */
async function streamSearch(
  query: string,
  signal: AbortSignal,
  handlers: { onMeta: (total: number) => void; onHit: (hit: LiveSearchHit) => void },
): Promise<void> {
  const response = await fetch(`/search.json?q=${encodeURIComponent(query)}`, {
    signal,
    headers: { accept: "application/x-ndjson" },
    credentials: "same-origin",
  });
  if (!response.ok || !response.body) throw new Error(`Search failed: ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleLine = (line: string): void => {
    const trimmed = line.trim();
    if (trimmed === "") return;
    const record = JSON.parse(trimmed) as
      | { kind: "meta"; total: number }
      | ({ kind: "hit" } & LiveSearchHit);
    if (record.kind === "meta") handlers.onMeta(record.total);
    else if (record.kind === "hit") handlers.onHit(record);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf("\n");
    while (newline !== -1) {
      handleLine(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf("\n");
    }
  }
  // Flush any trailing line that arrived without a closing newline.
  handleLine(buffer);
}

/**
 * Version picker (spec §18).
 *
 * The server renders a plain GET form; this submits it on change so a reader does not have to
 * press the button, and hides the button once the enhancement is in place. Without JavaScript
 * the form still works — the button is the fallback.
 */
function initVersionPicker(): void {
  for (const form of document.querySelectorAll<HTMLFormElement>("[data-version-picker]")) {
    const select = form.querySelector<HTMLSelectElement>("select[name=v]");
    const button = form.querySelector<HTMLButtonElement>("[data-version-go]");
    if (!select) continue;
    if (button) button.style.display = "none";
    select.addEventListener("change", () => form.submit());
  }
}

/**
 * The signed-in principal, or null. Fetched once and shared: both the header relabel and the
 * article Edit control need it, and the request is cheap for the common (anonymous) case —
 * with no session cookie the endpoint returns 401 without touching the database.
 */
interface MeUser {
  displayName: string;
  permissions: string[];
}

let mePromise: Promise<MeUser | null> | undefined;

function fetchMe(): Promise<MeUser | null> {
  if (!mePromise) {
    mePromise = fetch("/api/v1/auth/me", { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { user?: { displayName?: string; permissions?: string[] } } | null) => {
        const user = body?.user;
        if (!user?.displayName) return null;
        return { displayName: user.displayName, permissions: user.permissions ?? [] };
      })
      .catch(() => null);
  }
  return mePromise;
}

/**
 * Reflects the current session in the header sign-in link.
 *
 * The server renders a plain "Sign in" link that already works — this only relabels it with the
 * reader's display name once an active session is confirmed, so a signed-in contributor is not
 * told to sign in again.
 */
function initAccountLink(): void {
  const link = document.querySelector<HTMLAnchorElement>("[data-header-account]");
  if (!link) return;

  void fetchMe().then((me) => {
    if (me) {
      link.textContent = me.displayName;
      link.setAttribute("aria-label", `Signed in as ${me.displayName} — open the editor`);
    }
  });
}

/**
 * Adds an "Edit" control to an article for a signed-in reader who may edit it.
 *
 * The button is gated client-side (so the page HTML stays identical for everyone and remains
 * cacheable): it appears only when `/me` reports `page.suggest_edit` or `page.publish`. The
 * editor itself is a separate chunk, imported only on click, so a reader who never edits never
 * downloads it. `page.publish` holders publish live; everyone else submits for review — the
 * server is the real gate either way.
 */
function initEditAffordance(): void {
  const article = document.querySelector<HTMLElement>("article[data-page-id]");
  const slot = document.querySelector<HTMLElement>("[data-edit-slot]");
  const body = document.querySelector<HTMLElement>("[data-article-body]");
  const pageId = article?.getAttribute("data-page-id");
  if (!article || !slot || !body || !pageId) return;

  void fetchMe().then((me) => {
    if (!me) return;
    const canPublish = me.permissions.includes("page.publish");
    if (!canPublish && !me.permissions.includes("page.suggest_edit")) return;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "wiki-edit-button";
    button.textContent = "Edit";
    button.title = canPublish ? "Edit this page" : "Suggest an edit";
    button.addEventListener("click", () => {
      button.disabled = true;
      void import("./inline-editor")
        .then((m) => m.openInlineEditor({ pageId, canPublish, article, body, trigger: button }))
        .catch(() => {
          button.disabled = false;
        });
    });
    slot.append(button);

    // The Edit tab / a shared "?edit=1" link opens the editor straight away.
    if (new URLSearchParams(window.location.search).get("edit") === "1") button.click();
  });
}

/**
 * Builds a Watch / Watching toggle button. The caller supplies how to read the current state and
 * how to flip it, so the same control drives both page watches and product/category watches.
 */
function mountWatchToggle(
  slot: HTMLElement,
  endpoints: { check: string; add: () => Promise<Response>; remove: () => Promise<Response> },
): void {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "wiki-watch-button";
  let watching = false;
  const render = (): void => {
    button.textContent = watching ? "Watching" : "Watch";
    button.setAttribute("aria-pressed", String(watching));
    button.classList.toggle("wiki-watch-button--on", watching);
  };
  render();

  void fetch(endpoints.check, { credentials: "same-origin" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { watching?: boolean } | null) => {
      if (d) {
        watching = Boolean(d.watching);
        render();
      }
    });

  button.addEventListener("click", () => {
    button.disabled = true;
    void (watching ? endpoints.remove() : endpoints.add())
      .then((r) => {
        if (r.ok) {
          watching = !watching;
          render();
        }
      })
      .finally(() => {
        button.disabled = false;
      });
  });
  slot.append(button);
}

/**
 * Adds a Watch / Watching toggle to an article for a signed-in reader. Watching a page opts the
 * reader into notifications when it is edited or gains a discussion reply.
 */
function initWatchButton(): void {
  const article = document.querySelector<HTMLElement>("article[data-page-id]");
  const slot = document.querySelector<HTMLElement>("[data-edit-slot]");
  const pageId = article?.getAttribute("data-page-id");
  if (!article || !slot || !pageId) return;

  void fetchMe().then((me) => {
    if (!me) return;
    const id = encodeURIComponent(pageId);
    mountWatchToggle(slot, {
      check: `/api/v1/watches/${id}`,
      add: () =>
        fetch("/api/v1/watches", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ pageId }),
        }),
      remove: () => fetch(`/api/v1/watches/${id}`, { method: "DELETE", credentials: "same-origin" }),
    });
  });
}

/**
 * Adds a Watch toggle to a product or category hub for a signed-in reader. Watching a product or
 * category opts the reader into notifications when any page under it is published.
 */
function initEntityWatchButton(): void {
  const host = document.querySelector<HTMLElement>("[data-watch-entity][data-watch-entity-id]");
  const slot = document.querySelector<HTMLElement>("[data-watch-slot]");
  const entityType = host?.getAttribute("data-watch-entity");
  const entityId = host?.getAttribute("data-watch-entity-id");
  if (!host || !slot || !entityType || !entityId) return;

  void fetchMe().then((me) => {
    if (!me) return;
    const path = `/api/v1/watches/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`;
    mountWatchToggle(slot, {
      check: path,
      add: () =>
        fetch("/api/v1/watches/entity", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ entityType, entityId }),
        }),
      remove: () => fetch(path, { method: "DELETE", credentials: "same-origin" }),
    });
  });
}

/** Adds a notifications bell (with unread count) to the header for a signed-in reader. */
function initNotificationsBell(): void {
  const account = document.querySelector<HTMLElement>("[data-header-account]");
  if (!account) return;
  void fetchMe().then((me) => {
    if (!me) return;
    void fetch("/api/v1/notifications/count", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { unread?: number } | null) => {
        if (!d) return;
        const unread = d.unread ?? 0;
        const bell = document.createElement("a");
        bell.className = "brand-header__bell";
        bell.href = "/notifications";
        bell.setAttribute("aria-label", unread ? `Notifications, ${unread} unread` : "Notifications");
        // Inline SVG (not an emoji) so the icon matches the rest of the header chrome and inherits
        // the bar's ink colour. Static markup — the only variable, the count, is set via textContent.
        bell.innerHTML =
          '<svg class="brand-header__bell-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>';
        if (unread > 0) {
          bell.classList.add("brand-header__bell--unread");
          const badge = document.createElement("span");
          badge.className = "brand-header__bell-count";
          badge.textContent = unread > 99 ? "99+" : String(unread);
          bell.append(badge);
        }
        account.insertAdjacentElement("beforebegin", bell);
      });
  });
}

/** On the notifications page, wires "Mark all read" and the email-digest preference toggle. */
function initNotificationsPage(): void {
  const button = document.querySelector<HTMLButtonElement>("[data-mark-all-read]");
  if (button) {
    button.addEventListener("click", () => {
      button.disabled = true;
      void fetch("/api/v1/notifications/read", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: "{}",
      })
        .then((r) => {
          if (r.ok) window.location.reload();
          else button.disabled = false;
        })
        .catch(() => {
          button.disabled = false;
        });
    });
  }

  const pref = document.querySelector<HTMLElement>("[data-email-pref]");
  const input = document.querySelector<HTMLInputElement>("[data-email-pref-input]");
  if (!pref || !input) return;
  void fetch("/api/v1/notifications/preferences", { credentials: "same-origin" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { emailNotifications?: boolean } | null) => {
      if (!d) return;
      input.checked = d.emailNotifications !== false;
      pref.removeAttribute("hidden");
    });
  input.addEventListener("change", () => {
    input.disabled = true;
    void fetch("/api/v1/notifications/preferences", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ emailNotifications: input.checked }),
    })
      .catch(() => {
        input.checked = !input.checked;
      })
      .finally(() => {
        input.disabled = false;
      });
  });
}

/**
 * Wires the state-changing controls on the History and Discussion tab pages (revert, post,
 * resolve, lock, hide). These live in a separate chunk loaded only on a tab page, so the main
 * reader bundle carries none of it. The server re-checks every permission.
 */
function initWikiTabActions(): void {
  if (!document.querySelector("[data-wiki-tab-page]")) return;
  void import("./wiki-actions").then((m) => m.initWikiActions());
}

function init(): void {
  initSearchOverlay();
  initResponsiveArticleGrid();
  initTocHighlight();
  initVersionPicker();
  initAccountLink();
  initEditAffordance();
  initWatchButton();
  initEntityWatchButton();
  initNotificationsBell();
  initNotificationsPage();
  initWikiTabActions();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
